import { Select as SelectPrimitive } from "@kobalte/core/select";
import { Check, ChevronsUpDown, Plus } from "lucide-solid";
import { createMemo, type JSX, Show, splitProps } from "solid-js";
import { cn, reconcileById } from "@stockpilot/shared";

import { overlayPortalHost } from "./overlay-portal";

interface SelectOption {
  value: string;
  /** Display label. `null`/`undefined` render as an empty string (parity with native <option>). */
  label: string | null | undefined;
  disabled?: boolean;
}

/**
 * CTA rendered inside the dropdown when the Select has no options (AWS-style:
 * an empty combobox offers "Create X" / "Go to X" instead of a dead control).
 *
 * Pass `onSelect` to run an action (open a creation dialog) or `href` to render
 * a link (navigate to the screen that provides the options). Provide exactly one.
 * `can` (default `true`) gates the CTA on permission — when `false` the empty
 * dropdown falls back to a plain "no options" message and no creation affordance
 * is shown. Omit `emptyAction` entirely when the source is not creatable.
 */
export interface SelectEmptyAction {
  label: string;
  onSelect?: () => void;
  href?: string;
  /** Permission gate. When false, the CTA is hidden. Defaults to true. */
  can?: boolean;
  /** Leading icon for the CTA. Defaults to a plus glyph. */
  icon?: JSX.Element;
  /** Optional helper line shown above the CTA (e.g. "No products yet"). */
  description?: string;
}

interface SelectProps {
  /** Currently selected value (controlled). */
  value: string;
  /** Called with the new value when the selection changes. */
  onChange: (value: string) => void;
  /** Flat list of options to render in the listbox. */
  options: SelectOption[];
  /** Placeholder rendered when no value is selected. */
  placeholder?: string;
  /** Accessible label for the trigger. */
  "aria-label"?: string;
  /** Extra classes for the trigger button. */
  class?: string;
  /** Extra classes for the listbox content panel. */
  contentClass?: string;
  disabled?: boolean;
  /** Forwarded to the trigger so existing e2e selectors keep working. */
  "data-testid"?: string;
  /**
   * Shown inside the dropdown when `options` is empty. Lets the user jump to the
   * place that creates options instead of facing a dead Select. See
   * {@link SelectEmptyAction}.
   */
  emptyAction?: SelectEmptyAction;
  /** Message shown in the empty dropdown when there is no (permitted) CTA. */
  emptyLabel?: string;
}

/**
 * Premium Select built on `@kobalte/core` — replaces native `<select>` across
 * the app. Mirrors the native value/onChange contract (string value, string
 * callback) plus a flat `options` array so call-sites migrate mechanically.
 *
 * Theme-safe (accent / accent-foreground token pairs), animated panel,
 * full keyboard + typeahead + ARIA from Kobalte. Honours
 * `prefers-reduced-motion` via the global guard in global.css.
 */
export function Select(props: SelectProps): JSX.Element {
  const [local, others] = splitProps(props, [
    "value",
    "onChange",
    "options",
    "placeholder",
    "aria-label",
    "class",
    "contentClass",
    "disabled",
    "data-testid",
    "emptyAction",
    "emptyLabel",
  ]);

  // ── CC-26: identidad ESTABLE de las opciones (receta CC-23 dentro del propio
  // Select). Los call-sites suelen alimentar `options` con un `.map()` inline
  // sobre `query.data` — y las queries LIVE de PowerSync re-emiten arrays y
  // objetos con identidad NUEVA en cada tick aunque nada cambie. Kobalte
  // reconstruye su collection a partir de esas identidades y su Listbox
  // re-ejecuta `renderItem` por nodo (el `<Key by="key">` reutiliza la posición
  // pero el nodo/JSX del item se re-crea) → con el listbox ABIERTO, el DOM de
  // cada opción se RE-MONTA bajo el puntero y el click del usuario cae en un
  // nodo stale: la selección se descarta (valor vacío, submit disabled).
  // `reconcileById` colapsa cada re-emisión sin cambios reales al array/objetos
  // previos, así que la collection no se reconstruye y el click persiste.
  // Guardado por e2e/select-click.spec.ts (fixture /cc26-probe).
  const stableOptions = createMemo<SelectOption[]>((prev) =>
    reconcileById(prev ?? [], local.options, (o) => o.value),
  );

  const selectedOption = () => stableOptions().find((o) => o.value === local.value) ?? null;
  const isEmpty = () => stableOptions().length === 0;

  return (
    <SelectPrimitive<SelectOption>
      options={stableOptions()}
      optionValue="value"
      optionTextValue="label"
      optionDisabled="disabled"
      disabled={local.disabled}
      value={selectedOption()}
      // Single-select semantics: re-clicking the already-selected option must NOT
      // toggle it off. Kobalte's default lets a click on the current selection
      // emit `null` (deselect) — which here coerced the value back to "" and
      // silently cleared the field (e.g. re-picking the same product/location in
      // the stock-adjustment form wiped the selection before submit). This is a
      // required single-select, so disallow emptying it by interaction. The value
      // still starts empty (placeholder) and can always be CHANGED to another
      // option; it just can't be un-set by re-clicking.
      disallowEmptySelection
      onChange={(option) => {
        // With `disallowEmptySelection` Kobalte no longer emits null on a
        // re-click; keep the `?? ""` coercion as a defensive floor.
        local.onChange(option?.value ?? "");
      }}
      placeholder={local.placeholder}
      itemComponent={(itemProps) => (
        <SelectPrimitive.Item
          item={itemProps.item}
          class={cn(
            "relative flex cursor-pointer select-none items-center rounded-md py-2 pl-8 pr-3 text-sm outline-none transition-colors",
            "text-popover-foreground data-[highlighted]:bg-accent data-[highlighted]:text-accent-foreground",
            "data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
          )}
        >
          <SelectPrimitive.ItemIndicator class="absolute left-2 inline-flex items-center justify-center">
            <Check class="h-4 w-4" />
          </SelectPrimitive.ItemIndicator>
          <SelectPrimitive.ItemLabel>
            {itemProps.item.rawValue.label ?? ""}
          </SelectPrimitive.ItemLabel>
        </SelectPrimitive.Item>
      )}
    >
      <SelectPrimitive.Trigger
        aria-label={local["aria-label"]}
        data-testid={local["data-testid"]}
        class={cn(
          "inline-flex h-10 items-center justify-between gap-2 rounded-md border border-border bg-field-background px-3 text-sm",
          "outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-0",
          "data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50",
          "hover:border-accent/60",
          local.class,
        )}
        {...others}
      >
        <SelectPrimitive.Value<SelectOption> class="block truncate text-left">
          {(state) => (
            <Show
              when={state.selectedOption()}
              fallback={<span class="text-muted-foreground">{local.placeholder}</span>}
            >
              {state.selectedOption()?.label ?? ""}
            </Show>
          )}
        </SelectPrimitive.Value>
        <SelectPrimitive.Icon class="shrink-0 opacity-60">
          <ChevronsUpDown class="h-4 w-4" />
        </SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>
      {/* mount: host tagged data-live-announcer para que ariaHideOutside del
          Dialog modal NO oculte el listbox (CC-21) — ver overlay-portal.ts. */}
      <SelectPrimitive.Portal mount={overlayPortalHost()}>
        <SelectPrimitive.Content
          class={cn(
            "ui-select-content z-[250] overflow-hidden rounded-md border border-border bg-popover text-popover-foreground shadow-md outline-none",
            local.contentClass,
          )}
        >
          <Show
            when={!isEmpty()}
            fallback={<SelectEmptyContent action={local.emptyAction} label={local.emptyLabel} />}
          >
            <SelectPrimitive.Listbox class="max-h-72 overflow-y-auto p-1" />
          </Show>
        </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
    </SelectPrimitive>
  );
}

/**
 * Content shown inside an empty Select dropdown. Renders the `emptyAction` CTA
 * (creation button or navigation link) when present and permitted, otherwise a
 * muted "no options" message. The CTA closes the dropdown on activation.
 *
 * The CTA is keyboard-reachable (it is a native button/anchor, auto-focusable
 * inside the portalled panel) and inherits the panel's opacity-only entrance, so
 * it respects `prefers-reduced-motion` via the global guard like the rest of the UI.
 */
export function SelectEmptyContent(props: { action?: SelectEmptyAction; label?: string }) {
  const ctaClass = cn(
    "flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left text-sm font-medium outline-none",
    "text-popover-foreground transition-colors cursor-pointer",
    "hover:bg-accent hover:text-accent-foreground",
    "focus-visible:bg-accent focus-visible:text-accent-foreground focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring",
  );

  const ctaIcon = () => props.action?.icon ?? <Plus class="h-4 w-4 shrink-0 text-accent" />;

  return (
    <Show
      when={props.action && props.action.can !== false ? props.action : undefined}
      fallback={
        <div class="px-2.5 py-3 text-center text-sm text-muted-foreground">
          {/* Copy lives at the call site so this primitive stays i18n-clean; the
              neutral em-dash is the last-resort placeholder when no label is given. */}
          {props.label ?? "—"}
        </div>
      }
    >
      {(action) => (
        <div class="space-y-1">
          <Show when={action().description}>
            <p class="px-2.5 pt-1 text-xs text-muted-foreground">{action().description}</p>
          </Show>
          <Show
            when={action().href}
            fallback={
              <button
                type="button"
                class={ctaClass}
                onClick={() => {
                  action().onSelect?.();
                }}
              >
                {ctaIcon()}
                <span class="truncate">{action().label}</span>
              </button>
            }
          >
            <a href={action().href} class={ctaClass}>
              {ctaIcon()}
              <span class="truncate">{action().label}</span>
            </a>
          </Show>
        </div>
      )}
    </Show>
  );
}
